import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL, ENDPOINTS } from '../config';
import i18n from '../i18n';

interface CurrencyContextType {
  currency: string;
  setCurrency: (currency: string) => void;
  exchangeRates: { [key: string]: number };
  convertPrice: (price: number) => number;
  toUSD: (price: number) => number;
  toUSDFrom: (price: number, fromCurrency?: string | null) => number;
  formatPrice: (price: number, fromCurrency?: string | null) => string;
  availableCurrencies: string[];
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

interface CurrencyRateMasterDto {
  fromCurrencyName: string;
  toCurrencyName: string;
  currentRate: number;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    return localStorage.getItem('currency') || 'USD';
  });

  const setCurrency = (newCurrency: string) => {
    setCurrencyState(newCurrency);
    localStorage.setItem('currency', newCurrency);
  };
  const [exchangeRates, setExchangeRates] = useState<{ [key: string]: number }>({
    USD: 1
  });

  const normalizeCurrencyCode = (value?: string | null): string => {
    if (!value) return 'USD';
    const raw = value.trim();
    const normalized = raw.toUpperCase();
    const isoMatch = normalized.match(/\b[A-Z]{3}\b/);
    if (isoMatch?.[0]) return isoMatch[0];

    const n = normalized.replace(/\s+/g, ' ');
    const candidates: Array<[string, string[]]> = [
      ['USD', ['USD', 'US DOLLAR', 'DOLLAR', 'دولار', 'دولار امريكي', 'دولار أمريكي']],
    
      ['GBP', ['GBP', 'POUND', 'STERLING', 'جنيه استرليني', 'جنيه إسترليني']],
      ['SAR', ['SAR', 'RIYAL', 'ريال', 'ريال سعودي']],
      ['AED', ['AED', 'DIRHAM', 'درهم', 'درهم اماراتي', 'درهم إماراتي']],
      ['SDG', ['SDG', 'SUDANESE POUND', 'جنيه سوداني']],
      ['CNY', ['CNY', 'RMB', 'YUAN', 'يوان', 'رنمينبي']]
    ];
    for (const [code, keys] of candidates) {
      if (keys.some(k => n.includes(k.toUpperCase()))) return code;
    }
    return normalized;
  };

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CURRENCY_RATES}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });

        if (response.status === 401) {
          return;
        }

        if (response.ok) {
          const data: CurrencyRateMasterDto[] = await response.json();
          const edges: Record<string, Array<{ to: string; rate: number }>> = {};
          const addEdge = (from: string, to: string, rate: number) => {
            (edges[from] ||= []).push({ to, rate });
          };

          for (const rate of data) {
            const fromCode = normalizeCurrencyCode(rate.fromCurrencyName);
            const toCode = normalizeCurrencyCode(rate.toCurrencyName);
            const numericRate = Number(rate.currentRate);

            if (!fromCode || !toCode) continue;
            if (fromCode === toCode) continue;
            if (!Number.isFinite(numericRate) || numericRate <= 0) continue;

            addEdge(fromCode, toCode, numericRate);
            addEdge(toCode, fromCode, 1 * numericRate);
          }

          const derivedRates: { [key: string]: number } = { USD: 1 };
          const queue: string[] = ['USD'];

          for (let i = 0; i < queue.length; i++) {
            const current = queue[i];
            const currentRate = derivedRates[current];
            const nextEdges = edges[current] || [];

            for (const e of nextEdges) {
              if (derivedRates[e.to] !== undefined) continue;
              derivedRates[e.to] = currentRate * e.rate;
              queue.push(e.to);
            }
          }

          setExchangeRates(derivedRates);
        }
      } catch (error) {
        console.error('Failed to fetch currency rates:', error);
      }
    };

    fetchRates();
  }, []);

  const convertBetweenCurrencies = (amount: number, fromCurrency?: string | null, toCurrency?: string | null): number => {
    const fromCode = normalizeCurrencyCode(fromCurrency);
    const toCode = normalizeCurrencyCode(toCurrency);
    if (fromCode === toCode) return amount;

    const fromRate = fromCode === 'USD' ? 1 : exchangeRates[fromCode];
    const toRate = toCode === 'USD' ? 1 : exchangeRates[toCode];

    if (!fromRate || !toRate) return amount;

    const amountInUsd = fromCode === 'USD' ? amount : amount / fromRate;
    return toCode === 'USD' ? amountInUsd : amountInUsd * toRate;
  };

  const convertPrice = (price: number): number => {
    // Default to 1 if currency not found
    return convertBetweenCurrencies(price, 'USD', currency);
  };
  
  const toUSD = (price: number): number => {
    return convertBetweenCurrencies(price, currency, 'USD');
  };

  const toUSDFrom = (price: number, fromCurrency?: string | null): number => {
    return convertBetweenCurrencies(price, fromCurrency ?? 'USD', 'USD');
  };

  const formatPrice = (price: number, fromCurrency?: string | null): string => {
    const convertedPrice = convertBetweenCurrencies(price, fromCurrency ?? 'USD', currency);
    try {
      return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: currency,
      }).format(convertedPrice);
    } catch (e) {
      // Fallback if currency code is invalid
      return `${currency} ${convertedPrice.toFixed(2)}`;
    }
  };

  const availableCurrencies = Object.keys(exchangeRates);

  return (
    <CurrencyContext.Provider value={{ 
      currency, 
      setCurrency, 
      exchangeRates, 
      convertPrice, 
      toUSD,
      toUSDFrom,
      formatPrice,
      availableCurrencies
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};
